// ─── 国別・地域別地価係数（実際の公示地価・路線価データ基準）────────────────
//
// 【面積の基準】
//   ゲーム内面積はシューレース公式 + Haversine補正で計算した実際のkm²
//   1km² = 100 coins（基本価格）× 地価係数
//
// 【地価係数の根拠】
//   日本: 国土交通省 公示地価（令和6年）・路線価（令和5年）
//   海外: Global Property Guide, OECD Housing Data, IMF (2024)
//
// 【日本の地価係数計算式】
//   公示地価（円/m²）÷ 500,000 = 係数
//   例: 東京都千代田区 約25,000,000円/m² → 係数50.0
//       東京都新宿区   約5,000,000円/m²  → 係数10.0
//       大阪市中央区   約3,000,000円/m²  → 係数6.0
//
// 【海外の地価係数計算式】
//   USD/m² ÷ 500 = 係数
//   例: マンハッタン $25,000/m² → 係数50.0
//       ロンドン中心  $20,000/m² → 係数40.0

export interface CountryLandValue {
  country: string;
  flag: string;
  landMultiplier: number;     // 土地価格乗数（1km²=100coins × この値）
  houseIncome: number;        // 住宅1棟・居住者1人あたり coins/h
  companyIncome: number;      // 会社1棟あたり基本 coins/h
  powerPlantIncome: number;   // 発電所1棟あたり基本 coins/h
  houseCost: number;          // 住宅建設コスト coins
  companyCost: number;        // 会社設立コスト coins
  powerPlantCost: number;     // 発電所建設コスト coins
  avgLandPriceJpyM2?: number; // 参考: 平均地価 円/m²（日本のみ）
  avgLandPriceUsdM2?: number; // 参考: 平均地価 USD/m²（海外）
  description?: string;       // 地域説明
}

// ─────────────────────────────────────────────────────────────────────────────
// 日本の地価データ（国土交通省 令和6年公示地価・路線価ベース）
// ─────────────────────────────────────────────────────────────────────────────
//
// 【東京都 詳細区別データ】
// 千代田区（丸の内・大手町）: 最高路線価 約4,032万円/m²（令和5年）
// 中央区（銀座）:             最高路線価 約3,120万円/m²
// 港区（六本木・赤坂）:       最高路線価 約1,500万円/m²
// 新宿区（新宿駅周辺）:       最高路線価 約1,200万円/m²
// 渋谷区（渋谷・表参道）:     最高路線価 約1,000万円/m²
// 品川区・目黒区:             平均 約200-400万円/m²
// 世田谷区・杉並区:           平均 約80-150万円/m²
// 足立区・葛飾区:             平均 約30-60万円/m²
//
// 【大阪府 詳細データ】
// 大阪市中央区（心斎橋）:     最高路線価 約1,600万円/m²
// 大阪市北区（梅田）:         最高路線価 約1,400万円/m²
// 大阪市浪速区（難波）:       最高路線価 約800万円/m²
// 大阪市天王寺区:             平均 約200万円/m²
// 大阪市住吉区・平野区:       平均 約50-80万円/m²
//
// 【名古屋市 詳細データ】
// 名古屋市中区（栄・名駅）:   最高路線価 約1,200万円/m²
// 名古屋市東区・西区:         平均 約100-200万円/m²
// 名古屋市緑区・天白区:       平均 約40-80万円/m²

export const COUNTRY_LAND_VALUES: Record<string, CountryLandValue> = {

  // ══════════════════════════════════════════════════════════════════════════
  // 日本 - 東京都（区別詳細）
  // ══════════════════════════════════════════════════════════════════════════

  // 千代田区（丸の内・大手町・霞が関）
  // 公示地価: 約2,000万〜4,000万円/m²（商業地）
  JP_CHIYODA: {
    country: "千代田区", flag: "🏛️",
    landMultiplier: 60.0,
    houseIncome: 600, companyIncome: 12000, powerPlantIncome: 60000,
    houseCost: 30000, companyCost: 600000, powerPlantCost: 3000000,
    avgLandPriceJpyM2: 30000000,
    description: "丸の内・大手町・霞が関。日本最高地価地帯",
  },

  // 中央区（銀座・日本橋）
  // 公示地価: 約1,500万〜3,000万円/m²（銀座商業地）
  JP_CHUO: {
    country: "中央区（銀座）", flag: "💎",
    landMultiplier: 50.0,
    houseIncome: 500, companyIncome: 10000, powerPlantIncome: 50000,
    houseCost: 25000, companyCost: 500000, powerPlantCost: 2500000,
    avgLandPriceJpyM2: 25000000,
    description: "銀座・日本橋・築地。世界最高水準の商業地価",
  },

  // 港区（六本木・赤坂・麻布・青山）
  // 公示地価: 約500万〜1,500万円/m²
  JP_MINATO: {
    country: "港区", flag: "🗼",
    landMultiplier: 35.0,
    houseIncome: 350, companyIncome: 7000, powerPlantIncome: 35000,
    houseCost: 17500, companyCost: 350000, powerPlantCost: 1750000,
    avgLandPriceJpyM2: 17500000,
    description: "六本木・赤坂・麻布・青山・汐留",
  },

  // 新宿区（新宿駅周辺・歌舞伎町）
  // 公示地価: 約400万〜1,200万円/m²
  JP_SHINJUKU: {
    country: "新宿区", flag: "🌃",
    landMultiplier: 28.0,
    houseIncome: 280, companyIncome: 5600, powerPlantIncome: 28000,
    houseCost: 14000, companyCost: 280000, powerPlantCost: 1400000,
    avgLandPriceJpyM2: 14000000,
    description: "新宿駅・歌舞伎町・西新宿高層ビル街",
  },

  // 渋谷区（渋谷・表参道・原宿・恵比寿）
  // 公示地価: 約300万〜1,000万円/m²
  JP_SHIBUYA: {
    country: "渋谷区", flag: "🎌",
    landMultiplier: 25.0,
    houseIncome: 250, companyIncome: 5000, powerPlantIncome: 25000,
    houseCost: 12500, companyCost: 250000, powerPlantCost: 1250000,
    avgLandPriceJpyM2: 12500000,
    description: "渋谷・表参道・原宿・恵比寿・代官山",
  },

  // 文京区・台東区（上野・浅草・本郷）
  // 公示地価: 約100万〜300万円/m²
  JP_BUNKYO: {
    country: "文京区・台東区", flag: "🏯",
    landMultiplier: 12.0,
    houseIncome: 120, companyIncome: 2400, powerPlantIncome: 12000,
    houseCost: 6000, companyCost: 120000, powerPlantCost: 600000,
    avgLandPriceJpyM2: 6000000,
    description: "上野・浅草・本郷・湯島",
  },

  // 豊島区・北区・荒川区（池袋・王子）
  // 公示地価: 約80万〜200万円/m²
  JP_TOSHIMA: {
    country: "豊島区・北区", flag: "🏙️",
    landMultiplier: 9.0,
    houseIncome: 90, companyIncome: 1800, powerPlantIncome: 9000,
    houseCost: 4500, companyCost: 90000, powerPlantCost: 450000,
    avgLandPriceJpyM2: 4500000,
    description: "池袋・王子・赤羽・日暮里",
  },

  // 品川区・目黒区・大田区（品川・目黒・蒲田）
  // 公示地価: 約100万〜400万円/m²
  JP_SHINAGAWA: {
    country: "品川区・目黒区", flag: "🚅",
    landMultiplier: 14.0,
    houseIncome: 140, companyIncome: 2800, powerPlantIncome: 14000,
    houseCost: 7000, companyCost: 140000, powerPlantCost: 700000,
    avgLandPriceJpyM2: 7000000,
    description: "品川・目黒・五反田・大崎",
  },

  // 世田谷区・杉並区・中野区（三軒茶屋・吉祥寺方面）
  // 公示地価: 約60万〜150万円/m²
  JP_SETAGAYA: {
    country: "世田谷区・杉並区", flag: "🌿",
    landMultiplier: 7.0,
    houseIncome: 70, companyIncome: 1400, powerPlantIncome: 7000,
    houseCost: 3500, companyCost: 70000, powerPlantCost: 350000,
    avgLandPriceJpyM2: 3500000,
    description: "三軒茶屋・下北沢・高円寺・荻窪",
  },

  // 江東区・墨田区・江戸川区（東京東部）
  // 公示地価: 約40万〜100万円/m²
  JP_KOTO: {
    country: "江東区・墨田区", flag: "🏗️",
    landMultiplier: 5.0,
    houseIncome: 50, companyIncome: 1000, powerPlantIncome: 5000,
    houseCost: 2500, companyCost: 50000, powerPlantCost: 250000,
    avgLandPriceJpyM2: 2500000,
    description: "豊洲・錦糸町・亀戸・葛西",
  },

  // 足立区・葛飾区・板橋区（東京北東部）
  // 公示地価: 約25万〜60万円/m²
  JP_ADACHI: {
    country: "足立区・葛飾区", flag: "🏘️",
    landMultiplier: 3.0,
    houseIncome: 30, companyIncome: 600, powerPlantIncome: 3000,
    houseCost: 1500, companyCost: 30000, powerPlantCost: 150000,
    avgLandPriceJpyM2: 1500000,
    description: "北千住・亀有・西新井・板橋",
  },

  // 東京23区外（三鷹・武蔵野・八王子・立川など）
  // 公示地価: 約20万〜80万円/m²
  JP_TOKYO_SUBURB: {
    country: "東京市部", flag: "🌆",
    landMultiplier: 4.0,
    houseIncome: 40, companyIncome: 800, powerPlantIncome: 4000,
    houseCost: 2000, companyCost: 40000, powerPlantCost: 200000,
    avgLandPriceJpyM2: 2000000,
    description: "吉祥寺・三鷹・立川・八王子・町田",
  },

  // 神奈川県（横浜・川崎）
  // 公示地価: 横浜駅周辺 約200万〜500万円/m²、川崎 約150万〜400万円/m²
  JP_YOKOHAMA: {
    country: "横浜・川崎", flag: "⚓",
    landMultiplier: 10.0,
    houseIncome: 100, companyIncome: 2000, powerPlantIncome: 10000,
    houseCost: 5000, companyCost: 100000, powerPlantCost: 500000,
    avgLandPriceJpyM2: 5000000,
    description: "横浜駅・みなとみらい・川崎駅周辺",
  },

  // 神奈川県郊外（鎌倉・藤沢・相模原など）
  // 公示地価: 約20万〜80万円/m²
  JP_KANAGAWA: {
    country: "神奈川県", flag: "🌊",
    landMultiplier: 3.5,
    houseIncome: 35, companyIncome: 700, powerPlantIncome: 3500,
    houseCost: 1750, companyCost: 35000, powerPlantCost: 175000,
    avgLandPriceJpyM2: 1750000,
    description: "鎌倉・藤沢・相模原・厚木・小田原",
  },

  // 埼玉県（さいたま市・川口・越谷）
  // 公示地価: さいたま市大宮 約100万〜200万円/m²、郊外 約15万〜50万円/m²
  JP_SAITAMA_CITY: {
    country: "さいたま市", flag: "🏟️",
    landMultiplier: 5.0,
    houseIncome: 50, companyIncome: 1000, powerPlantIncome: 5000,
    houseCost: 2500, companyCost: 50000, powerPlantCost: 250000,
    avgLandPriceJpyM2: 2500000,
    description: "大宮・浦和・川口・越谷",
  },

  JP_SAITAMA: {
    country: "埼玉県", flag: "🌾",
    landMultiplier: 2.0,
    houseIncome: 20, companyIncome: 400, powerPlantIncome: 2000,
    houseCost: 1000, companyCost: 20000, powerPlantCost: 100000,
    avgLandPriceJpyM2: 1000000,
    description: "所沢・熊谷・秩父・春日部",
  },

  // 千葉県（千葉市・船橋・柏・浦安）
  // 公示地価: 千葉市中心 約80万〜150万円/m²、浦安 約100万〜200万円/m²
  JP_CHIBA_CITY: {
    country: "千葉市・浦安", flag: "🎡",
    landMultiplier: 4.5,
    houseIncome: 45, companyIncome: 900, powerPlantIncome: 4500,
    houseCost: 2250, companyCost: 45000, powerPlantCost: 225000,
    avgLandPriceJpyM2: 2250000,
    description: "千葉市・浦安・船橋・柏・松戸",
  },

  JP_CHIBA: {
    country: "千葉県", flag: "🌻",
    landMultiplier: 1.8,
    houseIncome: 18, companyIncome: 360, powerPlantIncome: 1800,
    houseCost: 900, companyCost: 18000, powerPlantCost: 90000,
    avgLandPriceJpyM2: 900000,
    description: "成田・木更津・銚子・館山",
  },

  // ══════════════════════════════════════════════════════════════════════════
  // 日本 - 大阪府（区別詳細）
  // ══════════════════════════════════════════════════════════════════════════

  // 大阪市中央区・北区（心斎橋・梅田・難波）
  // 公示地価: 約500万〜1,600万円/m²
  JP_OSAKA_CENTER: {
    country: "大阪市中心部", flag: "🏯",
    landMultiplier: 22.0,
    houseIncome: 220, companyIncome: 4400, powerPlantIncome: 22000,
    houseCost: 11000, companyCost: 220000, powerPlantCost: 1100000,
    avgLandPriceJpyM2: 11000000,
    description: "心斎橋・梅田・難波・天王寺",
  },

  // 大阪市東部・南部（鶴橋・住吉・平野）
  // 公示地価: 約30万〜100万円/m²
  JP_OSAKA_EAST: {
    country: "大阪市東南部", flag: "🏙️",
    landMultiplier: 4.0,
    houseIncome: 40, companyIncome: 800, powerPlantIncome: 4000,
    houseCost: 2000, companyCost: 40000, powerPlantCost: 200000,
    avgLandPriceJpyM2: 2000000,
    description: "鶴橋・住吉・平野・東大阪",
  },

  // 大阪府郊外（堺・豊中・吹田・高槻）
  // 公示地価: 約20万〜80万円/m²
  JP_OSAKA_SUBURB: {
    country: "大阪府郊外", flag: "🌆",
    landMultiplier: 3.0,
    houseIncome: 30, companyIncome: 600, powerPlantIncome: 3000,
    houseCost: 1500, companyCost: 30000, powerPlantCost: 150000,
    avgLandPriceJpyM2: 1500000,
    description: "堺・豊中・吹田・高槻・枚方",
  },

  // ══════════════════════════════════════════════════════════════════════════
  // 日本 - 愛知県（名古屋）
  // ══════════════════════════════════════════════════════════════════════════

  // 名古屋市中区・中村区（栄・名古屋駅）
  // 公示地価: 約300万〜1,200万円/m²
  JP_NAGOYA_CENTER: {
    country: "名古屋市中心部", flag: "🏙️",
    landMultiplier: 18.0,
    houseIncome: 180, companyIncome: 3600, powerPlantIncome: 18000,
    houseCost: 9000, companyCost: 180000, powerPlantCost: 900000,
    avgLandPriceJpyM2: 9000000,
    description: "名古屋駅・栄・伏見・金山",
  },

  // 名古屋市郊外（緑区・天白区・守山区）
  // 公示地価: 約20万〜80万円/m²
  JP_NAGOYA_SUBURB: {
    country: "名古屋市郊外", flag: "🌿",
    landMultiplier: 3.0,
    houseIncome: 30, companyIncome: 600, powerPlantIncome: 3000,
    houseCost: 1500, companyCost: 30000, powerPlantCost: 150000,
    avgLandPriceJpyM2: 1500000,
    description: "緑区・天白区・守山区・名東区",
  },

  // 愛知県郊外（豊田・岡崎・一宮）
  // 公示地価: 約10万〜40万円/m²
  JP_AICHI: {
    country: "愛知県", flag: "🚗",
    landMultiplier: 2.0,
    houseIncome: 20, companyIncome: 400, powerPlantIncome: 2000,
    houseCost: 1000, companyCost: 20000, powerPlantCost: 100000,
    avgLandPriceJpyM2: 1000000,
    description: "豊田・岡崎・一宮・豊橋",
  },

  // ══════════════════════════════════════════════════════════════════════════
  // 日本 - 京都府・兵庫県
  // ══════════════════════════════════════════════════════════════════════════

  // 京都市中心部（四条・河原町・烏丸）
  // 公示地価: 約200万〜800万円/m²
  JP_KYOTO_CENTER: {
    country: "京都市中心部", flag: "⛩️",
    landMultiplier: 12.0,
    houseIncome: 120, companyIncome: 2400, powerPlantIncome: 12000,
    houseCost: 6000, companyCost: 120000, powerPlantCost: 600000,
    avgLandPriceJpyM2: 6000000,
    description: "四条・河原町・烏丸・祇園",
  },

  // 京都市郊外（伏見・山科・右京）
  // 公示地価: 約15万〜60万円/m²
  JP_KYOTO: {
    country: "京都府", flag: "🍁",
    landMultiplier: 2.5,
    houseIncome: 25, companyIncome: 500, powerPlantIncome: 2500,
    houseCost: 1250, companyCost: 25000, powerPlantCost: 125000,
    avgLandPriceJpyM2: 1250000,
    description: "伏見・山科・右京・宇治",
  },

  // 神戸市中心部（三宮・元町・北野）
  // 公示地価: 約100万〜400万円/m²
  JP_KOBE_CENTER: {
    country: "神戸市中心部", flag: "⚓",
    landMultiplier: 8.0,
    houseIncome: 80, companyIncome: 1600, powerPlantIncome: 8000,
    houseCost: 4000, companyCost: 80000, powerPlantCost: 400000,
    avgLandPriceJpyM2: 4000000,
    description: "三宮・元町・北野・ポートアイランド",
  },

  // 兵庫県郊外（西宮・芦屋・尼崎・姫路）
  // 公示地価: 約20万〜100万円/m²
  JP_HYOGO: {
    country: "兵庫県", flag: "🏰",
    landMultiplier: 3.0,
    houseIncome: 30, companyIncome: 600, powerPlantIncome: 3000,
    houseCost: 1500, companyCost: 30000, powerPlantCost: 150000,
    avgLandPriceJpyM2: 1500000,
    description: "西宮・芦屋・尼崎・姫路・明石",
  },

  // ══════════════════════════════════════════════════════════════════════════
  // 日本 - 政令指定都市（地方）
  // ══════════════════════════════════════════════════════════════════════════

  // 福岡市中心部（天神・博多）
  // 公示地価: 約200万〜600万円/m²
  JP_FUKUOKA_CENTER: {
    country: "福岡市中心部", flag: "🎋",
    landMultiplier: 10.0,
    houseIncome: 100, companyIncome: 2000, powerPlantIncome: 10000,
    houseCost: 5000, companyCost: 100000, powerPlantCost: 500000,
    avgLandPriceJpyM2: 5000000,
    description: "天神・博多・中洲・薬院",
  },

  // 福岡市郊外・福岡県
  // 公示地価: 約10万〜60万円/m²
  JP_FUKUOKA: {
    country: "福岡県", flag: "🌸",
    landMultiplier: 2.5,
    houseIncome: 25, companyIncome: 500, powerPlantIncome: 2500,
    houseCost: 1250, companyCost: 25000, powerPlantCost: 125000,
    avgLandPriceJpyM2: 1250000,
    description: "福岡市郊外・北九州・久留米",
  },

  // 札幌市中心部（大通・すすきの）
  // 公示地価: 約100万〜400万円/m²
  JP_SAPPORO_CENTER: {
    country: "札幌市中心部", flag: "❄️",
    landMultiplier: 7.0,
    houseIncome: 70, companyIncome: 1400, powerPlantIncome: 7000,
    houseCost: 3500, companyCost: 70000, powerPlantCost: 350000,
    avgLandPriceJpyM2: 3500000,
    description: "大通・すすきの・札幌駅周辺",
  },

  // 北海道（札幌郊外・地方）
  // 公示地価: 約2万〜30万円/m²
  JP_HOKKAIDO: {
    country: "北海道", flag: "🐻",
    landMultiplier: 0.8,
    houseIncome: 8, companyIncome: 160, powerPlantIncome: 800,
    houseCost: 400, companyCost: 8000, powerPlantCost: 40000,
    avgLandPriceJpyM2: 400000,
    description: "函館・旭川・釧路・帯広・農村部",
  },

  // 仙台市中心部（一番町・仙台駅）
  // 公示地価: 約100万〜300万円/m²
  JP_SENDAI_CENTER: {
    country: "仙台市中心部", flag: "🌿",
    landMultiplier: 6.0,
    houseIncome: 60, companyIncome: 1200, powerPlantIncome: 6000,
    houseCost: 3000, companyCost: 60000, powerPlantCost: 300000,
    avgLandPriceJpyM2: 3000000,
    description: "仙台駅・一番町・国分町",
  },

  // 東北地方（仙台郊外・岩手・青森・秋田・山形・福島）
  // 公示地価: 約2万〜30万円/m²
  JP_TOHOKU: {
    country: "東北地方", flag: "🍎",
    landMultiplier: 0.8,
    houseIncome: 8, companyIncome: 160, powerPlantIncome: 800,
    houseCost: 400, companyCost: 8000, powerPlantCost: 40000,
    avgLandPriceJpyM2: 400000,
    description: "盛岡・青森・秋田・山形・福島・郡山",
  },

  // 広島市中心部（紙屋町・八丁堀）
  // 公示地価: 約100万〜300万円/m²
  JP_HIROSHIMA_CENTER: {
    country: "広島市中心部", flag: "🕊️",
    landMultiplier: 6.0,
    houseIncome: 60, companyIncome: 1200, powerPlantIncome: 6000,
    houseCost: 3000, companyCost: 60000, powerPlantCost: 300000,
    avgLandPriceJpyM2: 3000000,
    description: "紙屋町・八丁堀・広島駅周辺",
  },

  // 中国・四国地方
  // 公示地価: 約3万〜50万円/m²
  JP_CHUGOKU_SHIKOKU: {
    country: "中国・四国地方", flag: "🌊",
    landMultiplier: 1.0,
    houseIncome: 10, companyIncome: 200, powerPlantIncome: 1000,
    houseCost: 500, companyCost: 10000, powerPlantCost: 50000,
    avgLandPriceJpyM2: 500000,
    description: "岡山・松山・高松・高知・山口",
  },

  // 熊本市・鹿児島市・那覇市（九州地方都市）
  // 公示地価: 約50万〜200万円/m²
  JP_KYUSHU_CITY: {
    country: "九州地方都市", flag: "🌺",
    landMultiplier: 4.0,
    houseIncome: 40, companyIncome: 800, powerPlantIncome: 4000,
    houseCost: 2000, companyCost: 40000, powerPlantCost: 200000,
    avgLandPriceJpyM2: 2000000,
    description: "熊本・鹿児島・長崎・大分・宮崎",
  },

  // 沖縄県（那覇市中心部）
  // 公示地価: 約100万〜300万円/m²（近年急上昇）
  JP_NAHA_CENTER: {
    country: "那覇市中心部", flag: "🌺",
    landMultiplier: 6.0,
    houseIncome: 60, companyIncome: 1200, powerPlantIncome: 6000,
    houseCost: 3000, companyCost: 60000, powerPlantCost: 300000,
    avgLandPriceJpyM2: 3000000,
    description: "国際通り・那覇新都心・おもろまち",
  },

  // 沖縄県郊外・離島
  // 公示地価: 約5万〜50万円/m²
  JP_OKINAWA: {
    country: "沖縄県", flag: "🏝️",
    landMultiplier: 1.5,
    houseIncome: 15, companyIncome: 300, powerPlantIncome: 1500,
    houseCost: 750, companyCost: 15000, powerPlantCost: 75000,
    avgLandPriceJpyM2: 750000,
    description: "沖縄市・宜野湾・石垣島・宮古島",
  },

  // 日本農村・山間部
  // 公示地価: 約1万〜10万円/m²
  JP_RURAL: {
    country: "日本農村・山間部", flag: "🌾",
    landMultiplier: 0.3,
    houseIncome: 3, companyIncome: 60, powerPlantIncome: 300,
    houseCost: 150, companyCost: 3000, powerPlantCost: 15000,
    avgLandPriceJpyM2: 150000,
    description: "農村・山間部・過疎地域",
  },

  // ══════════════════════════════════════════════════════════════════════════
  // アジア太平洋
  // ══════════════════════════════════════════════════════════════════════════

  // 香港（世界最高水準）
  // 住宅地価: $30,000〜$80,000/m²（中環・山頂）
  HK_CENTER: {
    country: "香港中環・山頂", flag: "🇭🇰",
    landMultiplier: 80.0,
    houseIncome: 800, companyIncome: 16000, powerPlantIncome: 80000,
    houseCost: 40000, companyCost: 800000, powerPlantCost: 4000000,
    avgLandPriceUsdM2: 40000,
    description: "中環・山頂・銅鑼湾・尖沙咀",
  },

  HK: {
    country: "香港", flag: "🇭🇰",
    landMultiplier: 36.0,
    houseIncome: 360, companyIncome: 7200, powerPlantIncome: 36000,
    houseCost: 18000, companyCost: 360000, powerPlantCost: 1800000,
    avgLandPriceUsdM2: 18000,
    description: "香港全域",
  },

  // シンガポール
  // 住宅地価: $15,000〜$40,000/m²（オーチャード・マリーナ）
  SG_CENTER: {
    country: "シンガポール中心部", flag: "🇸🇬",
    landMultiplier: 50.0,
    houseIncome: 500, companyIncome: 10000, powerPlantIncome: 50000,
    houseCost: 25000, companyCost: 500000, powerPlantCost: 2500000,
    avgLandPriceUsdM2: 25000,
    description: "オーチャード・マリーナ・CBD",
  },

  SG: {
    country: "シンガポール", flag: "🇸🇬",
    landMultiplier: 22.0,
    houseIncome: 220, companyIncome: 4400, powerPlantIncome: 22000,
    houseCost: 11000, companyCost: 220000, powerPlantCost: 1100000,
    avgLandPriceUsdM2: 11000,
    description: "シンガポール全域",
  },

  // 韓国（ソウル）
  // 住宅地価: ソウル江南 $10,000〜$25,000/m²
  KR_GANGNAM: {
    country: "ソウル江南", flag: "🇰🇷",
    landMultiplier: 30.0,
    houseIncome: 300, companyIncome: 6000, powerPlantIncome: 30000,
    houseCost: 15000, companyCost: 300000, powerPlantCost: 1500000,
    avgLandPriceUsdM2: 15000,
    description: "江南・瑞草・松坡・龍山",
  },

  KR_SEOUL: {
    country: "ソウル", flag: "🇰🇷",
    landMultiplier: 16.0,
    houseIncome: 160, companyIncome: 3200, powerPlantIncome: 16000,
    houseCost: 8000, companyCost: 160000, powerPlantCost: 800000,
    avgLandPriceUsdM2: 8000,
    description: "ソウル全域",
  },

  KR: {
    country: "韓国", flag: "🇰🇷",
    landMultiplier: 6.0,
    houseIncome: 60, companyIncome: 1200, powerPlantIncome: 6000,
    houseCost: 3000, companyCost: 60000, powerPlantCost: 300000,
    avgLandPriceUsdM2: 3000,
    description: "釜山・仁川・大邱・大田",
  },

  // 中国
  CN_SHANGHAI: {
    country: "上海・北京中心部", flag: "🇨🇳",
    landMultiplier: 18.0,
    houseIncome: 180, companyIncome: 3600, powerPlantIncome: 18000,
    houseCost: 9000, companyCost: 180000, powerPlantCost: 900000,
    avgLandPriceUsdM2: 9000,
    description: "上海浦東・浦西・北京朝陽・海淀",
  },

  CN_SHENZHEN: {
    country: "深圳・広州", flag: "🇨🇳",
    landMultiplier: 12.0,
    houseIncome: 120, companyIncome: 2400, powerPlantIncome: 12000,
    houseCost: 6000, companyCost: 120000, powerPlantCost: 600000,
    avgLandPriceUsdM2: 6000,
    description: "深圳・広州・杭州・成都",
  },

  CN: {
    country: "中国", flag: "🇨🇳",
    landMultiplier: 4.0,
    houseIncome: 40, companyIncome: 800, powerPlantIncome: 4000,
    houseCost: 2000, companyCost: 40000, powerPlantCost: 200000,
    avgLandPriceUsdM2: 2000,
    description: "中国地方都市・農村部",
  },

  TW: {
    country: "台湾", flag: "🇹🇼",
    landMultiplier: 8.0,
    houseIncome: 80, companyIncome: 1600, powerPlantIncome: 8000,
    houseCost: 4000, companyCost: 80000, powerPlantCost: 400000,
    avgLandPriceUsdM2: 4000,
    description: "台北・新北・桃園・台中",
  },

  AU_SYDNEY: {
    country: "シドニー・メルボルン", flag: "🇦🇺",
    landMultiplier: 14.0,
    houseIncome: 140, companyIncome: 2800, powerPlantIncome: 14000,
    houseCost: 7000, companyCost: 140000, powerPlantCost: 700000,
    avgLandPriceUsdM2: 7000,
    description: "シドニーCBD・メルボルンCBD",
  },

  AU: {
    country: "オーストラリア", flag: "🇦🇺",
    landMultiplier: 6.0,
    houseIncome: 60, companyIncome: 1200, powerPlantIncome: 6000,
    houseCost: 3000, companyCost: 60000, powerPlantCost: 300000,
    avgLandPriceUsdM2: 3000,
    description: "ブリスベン・パース・アデレード",
  },

  NZ: {
    country: "ニュージーランド", flag: "🇳🇿",
    landMultiplier: 7.0,
    houseIncome: 70, companyIncome: 1400, powerPlantIncome: 7000,
    houseCost: 3500, companyCost: 70000, powerPlantCost: 350000,
    avgLandPriceUsdM2: 3500,
    description: "オークランド・ウェリントン",
  },

  IN_MUMBAI: {
    country: "ムンバイ・デリー", flag: "🇮🇳",
    landMultiplier: 8.0,
    houseIncome: 80, companyIncome: 1600, powerPlantIncome: 8000,
    houseCost: 4000, companyCost: 80000, powerPlantCost: 400000,
    avgLandPriceUsdM2: 4000,
    description: "ムンバイ南部・デリーConnaught Place",
  },

  IN: {
    country: "インド", flag: "🇮🇳",
    landMultiplier: 2.0,
    houseIncome: 20, companyIncome: 400, powerPlantIncome: 2000,
    houseCost: 1000, companyCost: 20000, powerPlantCost: 100000,
    avgLandPriceUsdM2: 1000,
    description: "バンガロール・チェンナイ・ハイデラバード",
  },

  ID: {
    country: "インドネシア", flag: "🇮🇩",
    landMultiplier: 1.5,
    houseIncome: 15, companyIncome: 300, powerPlantIncome: 1500,
    houseCost: 750, companyCost: 15000, powerPlantCost: 75000,
    avgLandPriceUsdM2: 750,
    description: "ジャカルタ・バリ・スラバヤ",
  },

  TH: {
    country: "タイ", flag: "🇹🇭",
    landMultiplier: 2.5,
    houseIncome: 25, companyIncome: 500, powerPlantIncome: 2500,
    houseCost: 1250, companyCost: 25000, powerPlantCost: 125000,
    avgLandPriceUsdM2: 1250,
    description: "バンコク・チェンマイ・パタヤ",
  },

  MY: {
    country: "マレーシア", flag: "🇲🇾",
    landMultiplier: 2.0,
    houseIncome: 20, companyIncome: 400, powerPlantIncome: 2000,
    houseCost: 1000, companyCost: 20000, powerPlantCost: 100000,
    avgLandPriceUsdM2: 1000,
    description: "クアラルンプール・ペナン",
  },

  PH: {
    country: "フィリピン", flag: "🇵🇭",
    landMultiplier: 1.2,
    houseIncome: 12, companyIncome: 240, powerPlantIncome: 1200,
    houseCost: 600, companyCost: 12000, powerPlantCost: 60000,
    avgLandPriceUsdM2: 600,
    description: "マニラ・セブ・ダバオ",
  },

  VN: {
    country: "ベトナム", flag: "🇻🇳",
    landMultiplier: 1.0,
    houseIncome: 10, companyIncome: 200, powerPlantIncome: 1000,
    houseCost: 500, companyCost: 10000, powerPlantCost: 50000,
    avgLandPriceUsdM2: 500,
    description: "ホーチミン・ハノイ",
  },

  // ══════════════════════════════════════════════════════════════════════════
  // ヨーロッパ
  // ══════════════════════════════════════════════════════════════════════════

  // ロンドン（世界最高水準）
  // 住宅地価: $20,000〜$60,000/m²（ナイツブリッジ・メイフェア）
  GB_LONDON_CENTER: {
    country: "ロンドン中心部", flag: "🇬🇧",
    landMultiplier: 60.0,
    houseIncome: 600, companyIncome: 12000, powerPlantIncome: 60000,
    houseCost: 30000, companyCost: 600000, powerPlantCost: 3000000,
    avgLandPriceUsdM2: 30000,
    description: "ナイツブリッジ・メイフェア・シティ・ウェストエンド",
  },

  GB_LONDON: {
    country: "ロンドン", flag: "🇬🇧",
    landMultiplier: 24.0,
    houseIncome: 240, companyIncome: 4800, powerPlantIncome: 24000,
    houseCost: 12000, companyCost: 240000, powerPlantCost: 1200000,
    avgLandPriceUsdM2: 12000,
    description: "ロンドン全域",
  },

  GB: {
    country: "イギリス", flag: "🇬🇧",
    landMultiplier: 8.0,
    houseIncome: 80, companyIncome: 1600, powerPlantIncome: 8000,
    houseCost: 4000, companyCost: 80000, powerPlantCost: 400000,
    avgLandPriceUsdM2: 4000,
    description: "マンチェスター・バーミンガム・エジンバラ",
  },

  // パリ
  // 住宅地価: $15,000〜$40,000/m²（1〜8区）
  FR_PARIS_CENTER: {
    country: "パリ中心部", flag: "🗼",
    landMultiplier: 40.0,
    houseIncome: 400, companyIncome: 8000, powerPlantIncome: 40000,
    houseCost: 20000, companyCost: 400000, powerPlantCost: 2000000,
    avgLandPriceUsdM2: 20000,
    description: "1〜8区・シャンゼリゼ・マレ地区",
  },

  FR_PARIS: {
    country: "パリ", flag: "🗼",
    landMultiplier: 18.0,
    houseIncome: 180, companyIncome: 3600, powerPlantIncome: 18000,
    houseCost: 9000, companyCost: 180000, powerPlantCost: 900000,
    avgLandPriceUsdM2: 9000,
    description: "パリ全域・近郊",
  },

  FR: {
    country: "フランス", flag: "🇫🇷",
    landMultiplier: 6.0,
    houseIncome: 60, companyIncome: 1200, powerPlantIncome: 6000,
    houseCost: 3000, companyCost: 60000, powerPlantCost: 300000,
    avgLandPriceUsdM2: 3000,
    description: "リヨン・マルセイユ・ニース・ボルドー",
  },

  // ドイツ
  DE_MUNICH: {
    country: "ミュンヘン・フランクフルト", flag: "🇩🇪",
    landMultiplier: 14.0,
    houseIncome: 140, companyIncome: 2800, powerPlantIncome: 14000,
    houseCost: 7000, companyCost: 140000, powerPlantCost: 700000,
    avgLandPriceUsdM2: 7000,
    description: "ミュンヘン・フランクフルト・ハンブルク",
  },

  DE: {
    country: "ドイツ", flag: "🇩🇪",
    landMultiplier: 6.0,
    houseIncome: 60, companyIncome: 1200, powerPlantIncome: 6000,
    houseCost: 3000, companyCost: 60000, powerPlantCost: 300000,
    avgLandPriceUsdM2: 3000,
    description: "ベルリン・ケルン・デュッセルドルフ",
  },

  // スイス（世界最高水準）
  CH_ZURICH: {
    country: "チューリッヒ・ジュネーブ", flag: "🇨🇭",
    landMultiplier: 30.0,
    houseIncome: 300, companyIncome: 6000, powerPlantIncome: 30000,
    houseCost: 15000, companyCost: 300000, powerPlantCost: 1500000,
    avgLandPriceUsdM2: 15000,
    description: "チューリッヒ・ジュネーブ・バーゼル",
  },

  CH: {
    country: "スイス", flag: "🇨🇭",
    landMultiplier: 14.0,
    houseIncome: 140, companyIncome: 2800, powerPlantIncome: 14000,
    houseCost: 7000, companyCost: 140000, powerPlantCost: 700000,
    avgLandPriceUsdM2: 7000,
    description: "スイス全域",
  },

  NL: {
    country: "オランダ", flag: "🇳🇱",
    landMultiplier: 10.0,
    houseIncome: 100, companyIncome: 2000, powerPlantIncome: 10000,
    houseCost: 5000, companyCost: 100000, powerPlantCost: 500000,
    avgLandPriceUsdM2: 5000,
    description: "アムステルダム・ロッテルダム・ハーグ",
  },

  SE: {
    country: "スウェーデン", flag: "🇸🇪",
    landMultiplier: 8.0,
    houseIncome: 80, companyIncome: 1600, powerPlantIncome: 8000,
    houseCost: 4000, companyCost: 80000, powerPlantCost: 400000,
    avgLandPriceUsdM2: 4000,
    description: "ストックホルム・ヨーテボリ",
  },

  NO: {
    country: "ノルウェー", flag: "🇳🇴",
    landMultiplier: 9.0,
    houseIncome: 90, companyIncome: 1800, powerPlantIncome: 9000,
    houseCost: 4500, companyCost: 90000, powerPlantCost: 450000,
    avgLandPriceUsdM2: 4500,
    description: "オスロ・ベルゲン",
  },

  DK: {
    country: "デンマーク", flag: "🇩🇰",
    landMultiplier: 8.0,
    houseIncome: 80, companyIncome: 1600, powerPlantIncome: 8000,
    houseCost: 4000, companyCost: 80000, powerPlantCost: 400000,
    avgLandPriceUsdM2: 4000,
    description: "コペンハーゲン・オーフス",
  },

  ES: {
    country: "スペイン", flag: "🇪🇸",
    landMultiplier: 5.0,
    houseIncome: 50, companyIncome: 1000, powerPlantIncome: 5000,
    houseCost: 2500, companyCost: 50000, powerPlantCost: 250000,
    avgLandPriceUsdM2: 2500,
    description: "マドリード・バルセロナ・バレンシア",
  },

  IT: {
    country: "イタリア", flag: "🇮🇹",
    landMultiplier: 5.0,
    houseIncome: 50, companyIncome: 1000, powerPlantIncome: 5000,
    houseCost: 2500, companyCost: 50000, powerPlantCost: 250000,
    avgLandPriceUsdM2: 2500,
    description: "ミラノ・ローマ・フィレンツェ",
  },

  PT: {
    country: "ポルトガル", flag: "🇵🇹",
    landMultiplier: 4.0,
    houseIncome: 40, companyIncome: 800, powerPlantIncome: 4000,
    houseCost: 2000, companyCost: 40000, powerPlantCost: 200000,
    avgLandPriceUsdM2: 2000,
    description: "リスボン・ポルト",
  },

  PL: {
    country: "ポーランド", flag: "🇵🇱",
    landMultiplier: 2.5,
    houseIncome: 25, companyIncome: 500, powerPlantIncome: 2500,
    houseCost: 1250, companyCost: 25000, powerPlantCost: 125000,
    avgLandPriceUsdM2: 1250,
    description: "ワルシャワ・クラクフ",
  },

  RU: {
    country: "ロシア", flag: "🇷🇺",
    landMultiplier: 3.0,
    houseIncome: 30, companyIncome: 600, powerPlantIncome: 3000,
    houseCost: 1500, companyCost: 30000, powerPlantCost: 150000,
    avgLandPriceUsdM2: 1500,
    description: "モスクワ・サンクトペテルブルク",
  },

  // ══════════════════════════════════════════════════════════════════════════
  // 北米
  // ══════════════════════════════════════════════════════════════════════════

  // ニューヨーク（マンハッタン）
  // 住宅地価: $20,000〜$60,000/m²（アッパーイーストサイド・トライベッカ）
  US_NYC_MANHATTAN: {
    country: "マンハッタン", flag: "🗽",
    landMultiplier: 60.0,
    houseIncome: 600, companyIncome: 12000, powerPlantIncome: 60000,
    houseCost: 30000, companyCost: 600000, powerPlantCost: 3000000,
    avgLandPriceUsdM2: 30000,
    description: "マンハッタン・ミッドタウン・ウォール街",
  },

  US_NYC: {
    country: "ニューヨーク", flag: "🗽",
    landMultiplier: 22.0,
    houseIncome: 220, companyIncome: 4400, powerPlantIncome: 22000,
    houseCost: 11000, companyCost: 220000, powerPlantCost: 1100000,
    avgLandPriceUsdM2: 11000,
    description: "ブルックリン・クイーンズ・ブロンクス",
  },

  // サンフランシスコ・シリコンバレー
  // 住宅地価: $10,000〜$30,000/m²
  US_SF: {
    country: "サンフランシスコ・シリコンバレー", flag: "🌉",
    landMultiplier: 28.0,
    houseIncome: 280, companyIncome: 5600, powerPlantIncome: 28000,
    houseCost: 14000, companyCost: 280000, powerPlantCost: 1400000,
    avgLandPriceUsdM2: 14000,
    description: "SF・パロアルト・サンノゼ・クパチーノ",
  },

  US_LA: {
    country: "ロサンゼルス", flag: "🌴",
    landMultiplier: 14.0,
    houseIncome: 140, companyIncome: 2800, powerPlantIncome: 14000,
    houseCost: 7000, companyCost: 140000, powerPlantCost: 700000,
    avgLandPriceUsdM2: 7000,
    description: "ビバリーヒルズ・サンタモニカ・ハリウッド",
  },

  US_MIAMI: {
    country: "マイアミ", flag: "🌊",
    landMultiplier: 10.0,
    houseIncome: 100, companyIncome: 2000, powerPlantIncome: 10000,
    houseCost: 5000, companyCost: 100000, powerPlantCost: 500000,
    avgLandPriceUsdM2: 5000,
    description: "マイアミビーチ・ブリックエル",
  },

  US_CHICAGO: {
    country: "シカゴ", flag: "🏙️",
    landMultiplier: 8.0,
    houseIncome: 80, companyIncome: 1600, powerPlantIncome: 8000,
    houseCost: 4000, companyCost: 80000, powerPlantCost: 400000,
    avgLandPriceUsdM2: 4000,
    description: "シカゴループ・リンカーンパーク",
  },

  US_SEATTLE: {
    country: "シアトル", flag: "☕",
    landMultiplier: 12.0,
    houseIncome: 120, companyIncome: 2400, powerPlantIncome: 12000,
    houseCost: 6000, companyCost: 120000, powerPlantCost: 600000,
    avgLandPriceUsdM2: 6000,
    description: "シアトル・ベルビュー・レドモンド",
  },

  US_BOSTON: {
    country: "ボストン", flag: "🎓",
    landMultiplier: 12.0,
    houseIncome: 120, companyIncome: 2400, powerPlantIncome: 12000,
    houseCost: 6000, companyCost: 120000, powerPlantCost: 600000,
    avgLandPriceUsdM2: 6000,
    description: "ボストン・ケンブリッジ",
  },

  US_DC: {
    country: "ワシントンDC", flag: "🏛️",
    landMultiplier: 14.0,
    houseIncome: 140, companyIncome: 2800, powerPlantIncome: 14000,
    houseCost: 7000, companyCost: 140000, powerPlantCost: 700000,
    avgLandPriceUsdM2: 7000,
    description: "DC・アーリントン・ベセスダ",
  },

  US: {
    country: "アメリカ", flag: "🇺🇸",
    landMultiplier: 4.0,
    houseIncome: 40, companyIncome: 800, powerPlantIncome: 4000,
    houseCost: 2000, companyCost: 40000, powerPlantCost: 200000,
    avgLandPriceUsdM2: 2000,
    description: "テキサス・フロリダ・中西部",
  },

  CA_TORONTO: {
    country: "トロント・バンクーバー", flag: "🇨🇦",
    landMultiplier: 14.0,
    houseIncome: 140, companyIncome: 2800, powerPlantIncome: 14000,
    houseCost: 7000, companyCost: 140000, powerPlantCost: 700000,
    avgLandPriceUsdM2: 7000,
    description: "トロント・バンクーバー",
  },

  CA: {
    country: "カナダ", flag: "🇨🇦",
    landMultiplier: 5.0,
    houseIncome: 50, companyIncome: 1000, powerPlantIncome: 5000,
    houseCost: 2500, companyCost: 50000, powerPlantCost: 250000,
    avgLandPriceUsdM2: 2500,
    description: "モントリオール・カルガリー・オタワ",
  },

  MX: {
    country: "メキシコ", flag: "🇲🇽",
    landMultiplier: 1.2,
    houseIncome: 12, companyIncome: 240, powerPlantIncome: 1200,
    houseCost: 600, companyCost: 12000, powerPlantCost: 60000,
    avgLandPriceUsdM2: 600,
    description: "メキシコシティ・モンテレイ・グアダラハラ",
  },

  // ══════════════════════════════════════════════════════════════════════════
  // 中東
  // ══════════════════════════════════════════════════════════════════════════

  AE_DUBAI: {
    country: "ドバイ中心部", flag: "🇦🇪",
    landMultiplier: 16.0,
    houseIncome: 160, companyIncome: 3200, powerPlantIncome: 16000,
    houseCost: 8000, companyCost: 160000, powerPlantCost: 800000,
    avgLandPriceUsdM2: 8000,
    description: "ダウンタウン・マリーナ・パームジュメイラ",
  },

  AE: {
    country: "UAE", flag: "🇦🇪",
    landMultiplier: 8.0,
    houseIncome: 80, companyIncome: 1600, powerPlantIncome: 8000,
    houseCost: 4000, companyCost: 80000, powerPlantCost: 400000,
    avgLandPriceUsdM2: 4000,
    description: "アブダビ・シャルジャ",
  },

  SA: {
    country: "サウジアラビア", flag: "🇸🇦",
    landMultiplier: 3.0,
    houseIncome: 30, companyIncome: 600, powerPlantIncome: 3000,
    houseCost: 1500, companyCost: 30000, powerPlantCost: 150000,
    avgLandPriceUsdM2: 1500,
    description: "リヤド・ジッダ・メッカ",
  },

  IL: {
    country: "イスラエル", flag: "🇮🇱",
    landMultiplier: 10.0,
    houseIncome: 100, companyIncome: 2000, powerPlantIncome: 10000,
    houseCost: 5000, companyCost: 100000, powerPlantCost: 500000,
    avgLandPriceUsdM2: 5000,
    description: "テルアビブ・エルサレム",
  },

  // ══════════════════════════════════════════════════════════════════════════
  // アフリカ・南米
  // ══════════════════════════════════════════════════════════════════════════

  ZA: {
    country: "南アフリカ", flag: "🇿🇦",
    landMultiplier: 1.5,
    houseIncome: 15, companyIncome: 300, powerPlantIncome: 1500,
    houseCost: 750, companyCost: 15000, powerPlantCost: 75000,
    avgLandPriceUsdM2: 750,
    description: "ヨハネスブルグ・ケープタウン",
  },

  NG: {
    country: "ナイジェリア", flag: "🇳🇬",
    landMultiplier: 0.8,
    houseIncome: 8, companyIncome: 160, powerPlantIncome: 800,
    houseCost: 400, companyCost: 8000, powerPlantCost: 40000,
    avgLandPriceUsdM2: 400,
    description: "ラゴス・アブジャ",
  },

  EG: {
    country: "エジプト", flag: "🇪🇬",
    landMultiplier: 0.6,
    houseIncome: 6, companyIncome: 120, powerPlantIncome: 600,
    houseCost: 300, companyCost: 6000, powerPlantCost: 30000,
    avgLandPriceUsdM2: 300,
    description: "カイロ・アレクサンドリア",
  },

  BR_SAO_PAULO: {
    country: "サンパウロ・リオ", flag: "🇧🇷",
    landMultiplier: 3.0,
    houseIncome: 30, companyIncome: 600, powerPlantIncome: 3000,
    houseCost: 1500, companyCost: 30000, powerPlantCost: 150000,
    avgLandPriceUsdM2: 1500,
    description: "サンパウロ・リオデジャネイロ",
  },

  BR: {
    country: "ブラジル", flag: "🇧🇷",
    landMultiplier: 1.0,
    houseIncome: 10, companyIncome: 200, powerPlantIncome: 1000,
    houseCost: 500, companyCost: 10000, powerPlantCost: 50000,
    avgLandPriceUsdM2: 500,
    description: "ブラジリア・ベロオリゾンテ",
  },

  AR: {
    country: "アルゼンチン", flag: "🇦🇷",
    landMultiplier: 0.8,
    houseIncome: 8, companyIncome: 160, powerPlantIncome: 800,
    houseCost: 400, companyCost: 8000, powerPlantCost: 40000,
    avgLandPriceUsdM2: 400,
    description: "ブエノスアイレス",
  },

  CL: {
    country: "チリ", flag: "🇨🇱",
    landMultiplier: 1.2,
    houseIncome: 12, companyIncome: 240, powerPlantIncome: 1200,
    houseCost: 600, companyCost: 12000, powerPlantCost: 60000,
    avgLandPriceUsdM2: 600,
    description: "サンティアゴ",
  },

  // ══════════════════════════════════════════════════════════════════════════
  // デフォルト
  // ══════════════════════════════════════════════════════════════════════════
  DEFAULT: {
    country: "その他", flag: "🌍",
    landMultiplier: 1.0,
    houseIncome: 10, companyIncome: 200, powerPlantIncome: 1000,
    houseCost: 500, companyCost: 10000, powerPlantCost: 50000,
    avgLandPriceUsdM2: 500,
    description: "未分類地域",
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// 座標から国・地域コードを推定（詳細版）
// ─────────────────────────────────────────────────────────────────────────────
export function getCountryCodeFromLatLng(lat: number, lng: number): string {

  // ══════════════════════════════════════════════════════════════════════════
  // 日本（詳細 - 市区町村レベル）
  // ══════════════════════════════════════════════════════════════════════════

  // 千代田区（丸の内・大手町・霞が関・神田）
  if (lat >= 35.670 && lat <= 35.700 && lng >= 139.740 && lng <= 139.780) return "JP_CHIYODA";

  // 中央区（銀座・日本橋・築地・月島）
  if (lat >= 35.655 && lat <= 35.685 && lng >= 139.760 && lng <= 139.800) return "JP_CHUO";

  // 港区（六本木・赤坂・麻布・青山・汐留・芝）
  if (lat >= 35.640 && lat <= 35.680 && lng >= 139.720 && lng <= 139.760) return "JP_MINATO";
  if (lat >= 35.655 && lat <= 35.680 && lng >= 139.700 && lng <= 139.730) return "JP_MINATO";

  // 新宿区（新宿駅・歌舞伎町・西新宿・四谷）
  if (lat >= 35.680 && lat <= 35.710 && lng >= 139.690 && lng <= 139.730) return "JP_SHINJUKU";

  // 渋谷区（渋谷・表参道・原宿・恵比寿・代官山）
  if (lat >= 35.645 && lat <= 35.680 && lng >= 139.690 && lng <= 139.720) return "JP_SHIBUYA";

  // 文京区・台東区（上野・浅草・本郷・湯島・秋葉原）
  if (lat >= 35.700 && lat <= 35.730 && lng >= 139.750 && lng <= 139.790) return "JP_BUNKYO";
  if (lat >= 35.700 && lat <= 35.720 && lng >= 139.780 && lng <= 139.810) return "JP_BUNKYO";

  // 豊島区・北区・荒川区（池袋・王子・赤羽・日暮里）
  if (lat >= 35.720 && lat <= 35.760 && lng >= 139.700 && lng <= 139.740) return "JP_TOSHIMA";
  if (lat >= 35.750 && lat <= 35.790 && lng >= 139.720 && lng <= 139.760) return "JP_TOSHIMA";

  // 品川区・目黒区（品川・目黒・五反田・大崎）
  if (lat >= 35.610 && lat <= 35.650 && lng >= 139.720 && lng <= 139.760) return "JP_SHINAGAWA";

  // 世田谷区・杉並区・中野区（三軒茶屋・下北沢・高円寺・荻窪）
  if (lat >= 35.630 && lat <= 35.680 && lng >= 139.640 && lng <= 139.700) return "JP_SETAGAYA";
  if (lat >= 35.680 && lat <= 35.720 && lng >= 139.620 && lng <= 139.680) return "JP_SETAGAYA";

  // 江東区・墨田区（豊洲・錦糸町・亀戸）
  if (lat >= 35.640 && lat <= 35.700 && lng >= 139.800 && lng <= 139.860) return "JP_KOTO";

  // 足立区・葛飾区・板橋区（北千住・亀有・西新井）
  if (lat >= 35.760 && lat <= 35.820 && lng >= 139.760 && lng <= 139.840) return "JP_ADACHI";
  if (lat >= 35.740 && lat <= 35.790 && lng >= 139.640 && lng <= 139.700) return "JP_ADACHI";

  // 東京23区全体（上記に当てはまらない23区内）
  if (lat >= 35.55 && lat <= 35.82 && lng >= 139.60 && lng <= 139.92) return "JP_KOTO";

  // 東京市部（三鷹・武蔵野・立川・八王子・町田）
  if (lat >= 35.55 && lat <= 35.82 && lng >= 139.30 && lng <= 139.60) return "JP_TOKYO_SUBURB";
  if (lat >= 35.40 && lat <= 35.60 && lng >= 139.40 && lng <= 139.60) return "JP_TOKYO_SUBURB";

  // 横浜市中心部（横浜駅・みなとみらい・関内）
  if (lat >= 35.440 && lat <= 35.480 && lng >= 139.620 && lng <= 139.660) return "JP_YOKOHAMA";

  // 川崎市中心部
  if (lat >= 35.520 && lat <= 35.560 && lng >= 139.690 && lng <= 139.730) return "JP_YOKOHAMA";

  // 神奈川県全体（横浜・川崎以外）
  if (lat >= 35.10 && lat <= 35.55 && lng >= 139.00 && lng <= 139.70) return "JP_KANAGAWA";

  // さいたま市（大宮・浦和）
  if (lat >= 35.840 && lat <= 35.920 && lng >= 139.580 && lng <= 139.680) return "JP_SAITAMA_CITY";

  // 埼玉県全体
  if (lat >= 35.80 && lat <= 36.30 && lng >= 138.80 && lng <= 139.90) return "JP_SAITAMA";

  // 千葉市・浦安（ディズニーランド周辺）
  if (lat >= 35.620 && lat <= 35.680 && lng >= 139.860 && lng <= 139.960) return "JP_CHIBA_CITY";
  if (lat >= 35.580 && lat <= 35.640 && lng >= 140.060 && lng <= 140.160) return "JP_CHIBA_CITY";

  // 千葉県全体
  if (lat >= 35.00 && lat <= 35.90 && lng >= 139.90 && lng <= 140.90) return "JP_CHIBA";

  // 大阪市中心部（心斎橋・梅田・難波・天王寺）
  if (lat >= 34.660 && lat <= 34.710 && lng >= 135.490 && lng <= 135.530) return "JP_OSAKA_CENTER";

  // 大阪市東南部（鶴橋・住吉・平野）
  if (lat >= 34.620 && lat <= 34.680 && lng >= 135.510 && lng <= 135.580) return "JP_OSAKA_EAST";

  // 大阪府郊外（堺・豊中・吹田・高槻・枚方）
  if (lat >= 34.50 && lat <= 34.80 && lng >= 135.30 && lng <= 135.65) return "JP_OSAKA_SUBURB";

  // 名古屋市中心部（名古屋駅・栄・伏見）
  if (lat >= 35.155 && lat <= 35.195 && lng >= 136.870 && lng <= 136.920) return "JP_NAGOYA_CENTER";

  // 名古屋市郊外
  if (lat >= 35.05 && lat <= 35.30 && lng >= 136.80 && lng <= 137.10) return "JP_NAGOYA_SUBURB";

  // 愛知県全体
  if (lat >= 34.60 && lat <= 35.40 && lng >= 136.70 && lng <= 137.80) return "JP_AICHI";

  // 京都市中心部（四条・河原町・烏丸・祇園）
  if (lat >= 34.990 && lat <= 35.020 && lng >= 135.750 && lng <= 135.790) return "JP_KYOTO_CENTER";

  // 京都府全体
  if (lat >= 34.70 && lat <= 35.80 && lng >= 135.40 && lng <= 136.00) return "JP_KYOTO";

  // 神戸市中心部（三宮・元町）
  if (lat >= 34.680 && lat <= 34.710 && lng >= 135.180 && lng <= 135.220) return "JP_KOBE_CENTER";

  // 兵庫県全体（西宮・芦屋・尼崎・姫路）
  if (lat >= 34.50 && lat <= 35.20 && lng >= 134.50 && lng <= 135.50) return "JP_HYOGO";

  // 福岡市中心部（天神・博多・中洲）
  if (lat >= 33.580 && lat <= 33.610 && lng >= 130.390 && lng <= 130.430) return "JP_FUKUOKA_CENTER";

  // 福岡県全体・北九州
  if (lat >= 33.30 && lat <= 34.00 && lng >= 130.00 && lng <= 131.20) return "JP_FUKUOKA";

  // 札幌市中心部（大通・すすきの・札幌駅）
  if (lat >= 43.050 && lat <= 43.080 && lng >= 141.340 && lng <= 141.380) return "JP_SAPPORO_CENTER";

  // 北海道全体
  if (lat >= 41.40 && lat <= 45.60 && lng >= 140.00 && lng <= 145.90) return "JP_HOKKAIDO";

  // 仙台市中心部（一番町・仙台駅）
  if (lat >= 38.255 && lat <= 38.280 && lng >= 140.860 && lng <= 140.900) return "JP_SENDAI_CENTER";

  // 東北地方全体
  if (lat >= 36.50 && lat <= 41.60 && lng >= 139.50 && lng <= 142.00) return "JP_TOHOKU";

  // 広島市中心部（紙屋町・八丁堀）
  if (lat >= 34.385 && lat <= 34.410 && lng >= 132.450 && lng <= 132.480) return "JP_HIROSHIMA_CENTER";

  // 中国・四国地方
  if (lat >= 33.00 && lat <= 35.50 && lng >= 130.50 && lng <= 134.50) return "JP_CHUGOKU_SHIKOKU";
  if (lat >= 32.80 && lat <= 34.50 && lng >= 130.00 && lng <= 134.00) return "JP_CHUGOKU_SHIKOKU";

  // 九州地方都市（熊本・鹿児島・長崎・大分・宮崎）
  if (lat >= 31.50 && lat <= 33.50 && lng >= 129.50 && lng <= 131.50) return "JP_KYUSHU_CITY";

  // 那覇市中心部（国際通り・新都心）
  if (lat >= 26.200 && lat <= 26.240 && lng >= 127.670 && lng <= 127.710) return "JP_NAHA_CENTER";

  // 沖縄県全体
  if (lat >= 24.00 && lat <= 27.10 && lng >= 122.90 && lng <= 131.00) return "JP_OKINAWA";

  // 日本全体（上記に当てはまらない地域 = 農村・山間部）
  if (lat >= 24.00 && lat <= 45.60 && lng >= 122.00 && lng <= 153.00) return "JP_RURAL";

  // ══════════════════════════════════════════════════════════════════════════
  // アジア太平洋
  // ══════════════════════════════════════════════════════════════════════════

  // 香港（中環・山頂・銅鑼湾）
  if (lat >= 22.26 && lat <= 22.30 && lng >= 114.14 && lng <= 114.20) return "HK_CENTER";
  if (lat >= 22.10 && lat <= 22.60 && lng >= 113.80 && lng <= 114.40) return "HK";

  // シンガポール（オーチャード・マリーナ・CBD）
  if (lat >= 1.270 && lat <= 1.310 && lng >= 103.820 && lng <= 103.870) return "SG_CENTER";
  if (lat >= 1.15 && lat <= 1.48 && lng >= 103.60 && lng <= 104.00) return "SG";

  // 韓国（ソウル江南）
  if (lat >= 37.490 && lat <= 37.540 && lng >= 127.010 && lng <= 127.080) return "KR_GANGNAM";
  if (lat >= 37.45 && lat <= 37.65 && lng >= 126.80 && lng <= 127.20) return "KR_SEOUL";
  if (lat >= 33.00 && lat <= 38.60 && lng >= 126.00 && lng <= 130.00) return "KR";

  // 中国（上海・北京）
  if (lat >= 31.10 && lat <= 31.40 && lng >= 121.30 && lng <= 121.70) return "CN_SHANGHAI";
  if (lat >= 39.75 && lat <= 40.05 && lng >= 116.20 && lng <= 116.60) return "CN_SHANGHAI";
  // 深圳・広州
  if (lat >= 22.40 && lat <= 22.80 && lng >= 113.70 && lng <= 114.30) return "CN_SHENZHEN";
  if (lat >= 22.90 && lat <= 23.40 && lng >= 113.00 && lng <= 113.60) return "CN_SHENZHEN";
  if (lat >= 18.00 && lat <= 55.00 && lng >= 73.00 && lng <= 135.00) return "CN";

  // 台湾
  if (lat >= 21.90 && lat <= 25.30 && lng >= 120.00 && lng <= 122.00) return "TW";

  // オーストラリア（シドニー・メルボルン）
  if (lat >= -34.00 && lat <= -33.70 && lng >= 151.00 && lng <= 151.30) return "AU_SYDNEY";
  if (lat >= -37.90 && lat <= -37.70 && lng >= 144.90 && lng <= 145.10) return "AU_SYDNEY";
  if (lat >= -44.00 && lat <= -10.00 && lng >= 113.00 && lng <= 154.00) return "AU";

  // ニュージーランド
  if (lat >= -47.00 && lat <= -34.00 && lng >= 166.00 && lng <= 178.00) return "NZ";

  // インド（ムンバイ・デリー）
  if (lat >= 18.85 && lat <= 19.25 && lng >= 72.75 && lng <= 73.05) return "IN_MUMBAI";
  if (lat >= 28.45 && lat <= 28.75 && lng >= 76.85 && lng <= 77.35) return "IN_MUMBAI";
  if (lat >= 6.00 && lat <= 37.00 && lng >= 68.00 && lng <= 97.00) return "IN";

  // 東南アジア
  if (lat >= -8.50 && lat <= 5.50 && lng >= 95.00 && lng <= 141.00) return "ID";
  if (lat >= 5.50 && lat <= 20.50 && lng >= 97.50 && lng <= 105.70) return "TH";
  if (lat >= 1.00 && lat <= 7.50 && lng >= 99.50 && lng <= 119.50) return "MY";
  if (lat >= 4.50 && lat <= 21.00 && lng >= 116.00 && lng <= 127.00) return "PH";
  if (lat >= 8.50 && lat <= 23.50 && lng >= 102.00 && lng <= 110.00) return "VN";

  // ══════════════════════════════════════════════════════════════════════════
  // ヨーロッパ
  // ══════════════════════════════════════════════════════════════════════════

  // ロンドン（ナイツブリッジ・メイフェア・シティ）
  if (lat >= 51.490 && lat <= 51.530 && lng >= -0.200 && lng <= -0.060) return "GB_LONDON_CENTER";
  if (lat >= 51.30 && lat <= 51.70 && lng >= -0.50 && lng <= 0.30) return "GB_LONDON";
  if (lat >= 49.90 && lat <= 60.90 && lng >= -8.20 && lng <= 1.80) return "GB";

  // パリ（1〜8区）
  if (lat >= 48.840 && lat <= 48.880 && lng >= 2.290 && lng <= 2.380) return "FR_PARIS_CENTER";
  if (lat >= 48.70 && lat <= 49.00 && lng >= 2.20 && lng <= 2.55) return "FR_PARIS";
  if (lat >= 41.30 && lat <= 51.10 && lng >= -5.20 && lng <= 9.60) return "FR";

  // ドイツ（ミュンヘン・フランクフルト・ハンブルク）
  if (lat >= 48.10 && lat <= 48.20 && lng >= 11.50 && lng <= 11.65) return "DE_MUNICH";
  if (lat >= 50.05 && lat <= 50.15 && lng >= 8.60 && lng <= 8.75) return "DE_MUNICH";
  if (lat >= 53.50 && lat <= 53.60 && lng >= 9.90 && lng <= 10.10) return "DE_MUNICH";
  if (lat >= 47.30 && lat <= 55.10 && lng >= 6.00 && lng <= 15.00) return "DE";

  // スイス（チューリッヒ・ジュネーブ）
  if (lat >= 47.35 && lat <= 47.42 && lng >= 8.50 && lng <= 8.58) return "CH_ZURICH";
  if (lat >= 46.18 && lat <= 46.24 && lng >= 6.12 && lng <= 6.18) return "CH_ZURICH";
  if (lat >= 45.80 && lat <= 47.80 && lng >= 6.00 && lng <= 10.50) return "CH";

  // オランダ
  if (lat >= 50.70 && lat <= 53.60 && lng >= 3.30 && lng <= 7.20) return "NL";

  // 北欧
  if (lat >= 55.30 && lat <= 69.10 && lng >= 11.00 && lng <= 24.20) return "SE";
  if (lat >= 57.90 && lat <= 71.20 && lng >= 4.50 && lng <= 31.20) return "NO";
  if (lat >= 54.50 && lat <= 57.80 && lng >= 8.00 && lng <= 15.20) return "DK";

  // スペイン・ポルトガル
  if (lat >= 36.00 && lat <= 43.80 && lng >= -9.30 && lng <= 3.30) return "ES";
  if (lat >= 36.90 && lat <= 42.20 && lng >= -9.50 && lng <= -6.20) return "PT";

  // イタリア
  if (lat >= 36.60 && lat <= 47.10 && lng >= 6.60 && lng <= 18.50) return "IT";

  // ポーランド
  if (lat >= 49.00 && lat <= 54.90 && lng >= 14.10 && lng <= 24.20) return "PL";

  // ロシア
  if (lat >= 41.20 && lat <= 81.90 && lng >= 19.60 && lng <= 180.00) return "RU";

  // ══════════════════════════════════════════════════════════════════════════
  // 北米
  // ══════════════════════════════════════════════════════════════════════════

  // マンハッタン（ミッドタウン・ウォール街）
  if (lat >= 40.700 && lat <= 40.780 && lng >= -74.020 && lng <= -73.960) return "US_NYC_MANHATTAN";
  if (lat >= 40.50 && lat <= 40.90 && lng >= -74.30 && lng <= -73.70) return "US_NYC";

  // サンフランシスコ・シリコンバレー
  if (lat >= 37.30 && lat <= 37.85 && lng >= -122.60 && lng <= -121.80) return "US_SF";

  // ロサンゼルス（ビバリーヒルズ・サンタモニカ）
  if (lat >= 33.90 && lat <= 34.20 && lng >= -118.50 && lng <= -118.10) return "US_LA";

  // マイアミ
  if (lat >= 25.60 && lat <= 25.90 && lng >= -80.40 && lng <= -80.10) return "US_MIAMI";

  // シカゴ
  if (lat >= 41.70 && lat <= 42.10 && lng >= -87.90 && lng <= -87.50) return "US_CHICAGO";

  // シアトル・ベルビュー
  if (lat >= 47.40 && lat <= 47.80 && lng >= -122.50 && lng <= -122.10) return "US_SEATTLE";

  // ボストン・ケンブリッジ
  if (lat >= 42.30 && lat <= 42.50 && lng >= -71.20 && lng <= -70.90) return "US_BOSTON";

  // ワシントンDC
  if (lat >= 38.80 && lat <= 39.00 && lng >= -77.20 && lng <= -76.90) return "US_DC";

  // アメリカ全体
  if (lat >= 24.50 && lat <= 49.40 && lng >= -125.00 && lng <= -66.90) return "US";

  // カナダ（トロント・バンクーバー）
  if (lat >= 43.60 && lat <= 43.80 && lng >= -79.50 && lng <= -79.30) return "CA_TORONTO";
  if (lat >= 49.20 && lat <= 49.40 && lng >= -123.20 && lng <= -123.00) return "CA_TORONTO";
  if (lat >= 42.00 && lat <= 83.00 && lng >= -141.00 && lng <= -52.60) return "CA";

  // メキシコ
  if (lat >= 14.50 && lat <= 32.70 && lng >= -117.10 && lng <= -86.70) return "MX";

  // ══════════════════════════════════════════════════════════════════════════
  // 中東・アフリカ・南米
  // ══════════════════════════════════════════════════════════════════════════

  // ドバイ（ダウンタウン・マリーナ）
  if (lat >= 25.10 && lat <= 25.30 && lng >= 55.10 && lng <= 55.30) return "AE_DUBAI";
  if (lat >= 22.60 && lat <= 26.10 && lng >= 51.60 && lng <= 56.40) return "AE";

  // サウジアラビア
  if (lat >= 16.40 && lat <= 32.20 && lng >= 36.50 && lng <= 55.70) return "SA";

  // イスラエル
  if (lat >= 29.50 && lat <= 33.30 && lng >= 34.30 && lng <= 35.90) return "IL";

  // 南アフリカ
  if (lat >= -34.90 && lat <= -22.10 && lng >= 16.50 && lng <= 32.90) return "ZA";

  // ナイジェリア
  if (lat >= 4.30 && lat <= 13.90 && lng >= 2.70 && lng <= 14.70) return "NG";

  // エジプト
  if (lat >= 22.00 && lat <= 31.70 && lng >= 24.70 && lng <= 37.00) return "EG";

  // ブラジル（サンパウロ・リオ）
  if (lat >= -23.70 && lat <= -22.80 && lng >= -46.80 && lng <= -46.40) return "BR_SAO_PAULO";
  if (lat >= -23.10 && lat <= -22.70 && lng >= -43.40 && lng <= -43.10) return "BR_SAO_PAULO";
  if (lat >= -33.80 && lat <= 5.30 && lng >= -73.90 && lng <= -34.80) return "BR";

  // アルゼンチン
  if (lat >= -55.00 && lat <= -21.80 && lng >= -73.60 && lng <= -53.60) return "AR";

  // チリ
  if (lat >= -55.90 && lat <= -17.50 && lng >= -75.70 && lng <= -66.40) return "CL";

  return "DEFAULT";
}

// ─── 座標から地価データを取得 ────────────────────────────────
export function getLandValueByLatLng(lat: number, lng: number): CountryLandValue {
  const code = getCountryCodeFromLatLng(lat, lng);
  return COUNTRY_LAND_VALUES[code] ?? COUNTRY_LAND_VALUES["DEFAULT"];
}

// ─── 土地購入価格を計算 ──────────────────────────────────────
// 基本: 1km² = 100 coins × 地価係数
// 例: 東京千代田区 0.01km² = 0.01 × 100 × 60 = 60 coins
export function calcLandPrice(areaKm2: number, lat: number, lng: number): number {
  const lv = getLandValueByLatLng(lat, lng);
  return Math.ceil(areaKm2 * 100 * lv.landMultiplier);
}

// ─── 地価係数の説明テキスト ──────────────────────────────────
export function getLandValueDescription(lat: number, lng: number): string {
  const lv = getLandValueByLatLng(lat, lng);
  const pricePerM2 = lv.avgLandPriceJpyM2
    ? `約${(lv.avgLandPriceJpyM2 / 10000).toFixed(0)}万円/m²`
    : lv.avgLandPriceUsdM2
    ? `約$${lv.avgLandPriceUsdM2.toLocaleString()}/m²`
    : "";
  return `${lv.flag} ${lv.country}${pricePerM2 ? ` (${pricePerM2})` : ""}`;
}
